import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { BackendApiService } from "../../../../app/services/backend-api.service";
import { BaseUrlConstantsService } from "../../../../app/services/base-url-constants.service";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";

export interface DoorComment {
  comment: string;
  supervisor_name: string;
  time: string;
  commented_on?: string; // Optional for job comments
}

@Component({
  selector: "door-open-comments-modal",
  templateUrl: "./door-open-comments-modal.componet.html",
  styleUrl: "./door-open-comments-modal.componet.scss",
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
})
export class DoorOpenCommentsModal {
  @Input("client_id") client_id: number;
  @Input("oven_id") oven_id: number;
  @Input("isVisible") isVisible: boolean = false;
  @Input("selected_door_id") selected_door_id: number | null = null;
  @Input("comment_type") comment_type: any = "job"; // NEW: Toggle for job comments mode
  @Output() onCancel = new EventEmitter();

  public baseOvenUrl = BaseUrlConstantsService.OVEN_BASE_URL;
  public comments: DoorComment[] = [];
  public isLoading: boolean = false;
  public hasError: boolean = false;
  public errorMessage: string = "";

  constructor(private srv: BackendApiService) {}

  ngOnInit() {
    if (this.client_id && this.oven_id) {
      this.loadComments();
    }
  }

  ngOnChanges() {
    if (this.isVisible && this.client_id && this.oven_id) {
      this.loadComments();
    }
  }

  loadComments() {
    if (this.comment_type == "job") {
      //job comments
      this.fetchJobComments();
    } else if (this.comment_type == "door") {
      //door open comments
      this.fetchComments();
    } else {
      //thresholds
      this.fetchThresholdsComments();
    }
  }

  fetchComments() {
    this.resetState();
    this.srv
      .getDataV2(
        `${this.baseOvenUrl}api/v1/oven-assets/get-door-comments/${this.client_id}/${this.oven_id}?door_status_id=${this.selected_door_id}`
        // `http://localhost:3024/api/v1/oven-assets/get-door-comments/${this.client_id}/${this.oven_id}?door_status_id=${this.selected_door_id}`
      )
      .then((data: any) => {
        const { code, status, data: responseData } = data;
        if (code === 200 && status === "success") {
          this.comments = responseData || [];
        } else {
          this.hasError = true;
          this.errorMessage = "Failed to load door comments";
        }
      })
      .catch((err) => {
        console.error("Error fetching door comments:", err);
        this.hasError = true;
        this.errorMessage = "Error loading door comments. Please try again.";
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  fetchJobComments() {
    this.resetState();
    // Example endpoint — adjust as needed
    this.srv
      .getDataV2(
        `${this.baseOvenUrl}api/v1/oven-assets/get-job-comments/${this.client_id}/${this.oven_id}?job_id=${this.selected_door_id}`
        // `http://localhost:3024/api/v1/oven-assets/get-job-comments/${this.client_id}/${this.oven_id}`
      )
      .then((data: any) => {
        const { code, status, data: responseData } = data;
        if (code === 200 && status === "success") {
          this.comments = responseData || [];
        } else {
          this.hasError = true;
          this.errorMessage = "Failed to load job comments";
        }
      })
      .catch((err) => {
        console.error("Error fetching job comments:", err);
        this.hasError = true;
        this.errorMessage = "Error loading job comments. Please try again.";
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  fetchThresholdsComments() {
    this.resetState();
    // Example endpoint — adjust as needed
    this.srv
      .getDataV2(
        `${this.baseOvenUrl}api/v1/oven-assets/get-threshold-comments/${this.client_id}/${this.oven_id}?job_id=${this.selected_door_id}`
        // `http://localhost:3024/api/v1/oven-assets/get-threshold-comments/${this.client_id}/${this.oven_id}`
      )
      .then((data: any) => {
        const { code, status, data: responseData } = data;
        if (code === 200 && status === "success") {
          this.comments = responseData || [];
        } else {
          this.hasError = true;
          this.errorMessage = "Failed to load job comments";
        }
      })
      .catch((err) => {
        console.error("Error fetching job comments:", err);
        this.hasError = true;
        this.errorMessage = "Error loading job comments. Please try again.";
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  resetState() {
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = "";
    this.comments = [];
  }

  formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  }

  formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }

  closeModalBox() {
    this.onCancel.emit();
  }

  refreshComments() {
    this.loadComments();
  }

  changeCommentType(type) {
    if (type == "job") {
      this.fetchJobComments();
      this.comment_type = "job";
    } else if (type == "door") {
      this.fetchComments();
      this.comment_type = "door";
    } else if (type == "thresholds") {
      this.comment_type = "thresholds";
      this.fetchThresholdsComments();
    }
  }
}
